import React, { useEffect } from "react";
import { FaRecycle, FaUsers, FaHeart, FaPhoneAlt } from "react-icons/fa";
import { Link } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import { Helmet } from "react-helmet-async";

function About() {
  useEffect(() => {
    AOS.init({ duration: 1000 });
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 mt-16 font-sans">
      <Helmet>
        <title>About Us | Pak Fashions - Fashion with Heart in Kenya</title>
        <meta
          name="description"
          content="Learn about Pak Fashions – our story, mission, and how we're redefining sustainable fashion in Nanyuki, Laikipia, and beyond."
        />
        <meta
          name="keywords"
          content="about pak fashions, sustainable fashion kenya, affordable clothes Nanyuki, Laikipia fashion, eco-friendly clothes"
        />
        <meta name="author" content="Pak Fashions" />
        <meta name="robots" content="index, follow" />

        {/* Open Graph */}
        <meta property="og:title" content="About Us | Pak Fashions" />
        <meta
          property="og:description"
          content="Learn about our journey, values, and mission to deliver affordable, sustainable style."
        />
        <meta
          property="og:image"
          content="https://pakfashions.co.ke/PakFashions-logo.jpg"
        />
        <meta property="og:url" content="https://pakfashions.co.ke/about" />
        <meta property="og:type" content="website" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About Pak Fashions" />
        <meta
          name="twitter:description"
          content="Pak Fashions - Where style meets sustainability in Kenya."
        />
        <meta
          name="twitter:image"
          content="https://pakfashions.co.ke/PakFashions-logo.jpg"
        />

        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Pak Fashions",
            url: "https://pakfashions.co.ke",
            logo: "https://pakfashions.co.ke/pak-circle.png",
            foundingDate: "2010",
            description:
              "Pak Fashions is a sustainable clothing brand based in Nanyuki, Kenya, offering affordable fashion for all.",
            address: {
              "@type": "PostalAddress",
              streetAddress: "237G+MQ6, Nanyuki",
              addressLocality: "Nanyuki",
              addressRegion: "Laikipia",
              postalCode: "10400",
              addressCountry: "KE",
            },
            contactPoint: {
              "@type": "ContactPoint",
              telephone: "+254 726 329 260",
              contactType: "Customer Support",
              areaServed: "KE",
              availableLanguage: ["English", "Swahili"],
            },
            sameAs: [
              "https://web.facebook.com/PAKFASHIONSKE",
              "https://www.instagram.com/pakfashionske?igsh=MXZsbXd3YnRhamltYg==",
            ],
          })}
        </script>
      </Helmet>
      
      {/* Hero Section */}
      <section className="relative bg-white pt-24 pb-32 px-6 rounded-b-[4rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden mb-24 border-b border-gray-100">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-yellow-100 rounded-full blur-[120px] opacity-40 pointer-events-none transform translate-x-1/3 -translate-y-1/3"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-yellow-100 rounded-full blur-[120px] opacity-40 pointer-events-none transform -translate-x-1/3 translate-y-1/3"></div>
        
        <div className="max-w-5xl mx-auto text-center relative z-10" data-aos="fade-down">
          <h1 className="text-5xl md:text-7xl font-black mb-8 tracking-tight text-gray-900 leading-[1.1]">
            Fashion with <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-yellow-600">Purpose</span>
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto text-gray-500 leading-relaxed font-medium">
            Quality loved fashion that brings people together — making style affordable,
            sustainable, and deeply community-driven.
          </p>
          <div className="w-24 h-1.5 bg-yellow-400 mx-auto mt-12 rounded-full shadow-sm" />
        </div>
      </section>

      {/* Our Story */}
      <section className="max-w-7xl mx-auto px-6 mb-32 grid md:grid-cols-2 gap-16 lg:gap-24 items-center">
        <div className="relative group" data-aos="fade-right">
          <div className="absolute -inset-4 bg-gradient-to-br from-yellow-100 to-transparent rounded-[3rem] opacity-50 group-hover:opacity-100 group-hover:scale-[1.02] transition-all duration-700 blur"></div>
          <div className="relative rounded-[2.5rem] bg-white p-2 shadow-xl border border-gray-100 transform hover:scale-[1.01] transition-transform duration-500">
            <img
              src="https://images.unsplash.com/photo-1521334884684-d80222895322?auto=format&fit=crop&w=800&q=80"
              alt="Pak Fashion Store"
              className="rounded-[2.2rem] w-full object-cover h-[30rem]"
            />
          </div>
        </div>
        
        <div data-aos="fade-left" className="space-y-8">
          <div>
            <span className="text-sm font-bold uppercase tracking-widest text-yellow-600 mb-3 block">Est. 2010</span>
            <h2 className="text-4xl md:text-5xl font-black tracking-tight text-gray-900 leading-tight">
              Our Story
            </h2>
          </div>
          <div className="space-y-6 text-gray-600 text-lg leading-relaxed font-medium">
            <p className="first-letter:text-5xl first-letter:font-black first-letter:text-gray-900 first-letter:mr-2 first-letter:float-left">
              Founded in 2010, Pak Fashion is dedicated to providing high-quality,
              affordable clothing to our community. We believe in extending the
              life of fashion while promoting sustainability and beauty.
            </p>
            <p>
              Our carefully curated collection supports eco-friendly shopping
              habits and helps customers find unique styles without compromising
              their budget or the planet. We aim to bring a human touch to every
              aspect of our business, ensuring that every garment tells a story
              of care, longevity, and community support.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="max-w-7xl mx-auto px-6 mb-32" data-aos="fade-up">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-black mb-6 tracking-tight text-gray-900">
            What We <span className="text-yellow-500">Stand For</span>
          </h2>
          <p className="text-xl text-gray-500 font-medium">The core values that guide everything we do.</p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8">
          {[
            {
              icon: <FaRecycle className="text-5xl text-yellow-500 mb-8 mx-auto group-hover:rotate-180 transition-transform duration-700" />,
              title: "Sustainability",
              desc: "Giving the community a chance to be beautiful while protecting our planet. We believe in fashion that leaves a smaller footprint.",
            },
            {
              icon: <FaUsers className="text-5xl text-yellow-500 mb-8 mx-auto group-hover:scale-110 transition-transform duration-500" />,
              title: "Community",
              desc: "Supporting local families and fostering connections through affordable, premium quality clothing that brings people together.",
            },
            {
              icon: <FaHeart className="text-5xl text-yellow-500 mb-8 mx-auto group-hover:text-red-500 transition-colors duration-500" />,
              title: "Passion",
              desc: "Passionate about fashion that's not only stylish, but ethically sourced and financially responsible for everyone.",
            },
          ].map(({ icon, title, desc }, i) => (
            <div
              key={i}
              className="bg-white p-12 rounded-[2.5rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-2xl transition-all duration-500 border border-gray-100 group hover:-translate-y-2 relative overflow-hidden"
              data-aos="fade-up"
              data-aos-delay={i * 100}
            >
              <div className="absolute top-0 left-0 w-full h-1.5 bg-yellow-400 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
              {icon}
              <h3 className="text-2xl font-black mb-4 text-center text-gray-900">{title}</h3>
              <p className="text-gray-500 leading-relaxed text-center font-medium">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Why Choose Us */}
      <section
        className="relative max-w-6xl mx-auto mb-32 px-6"
        data-aos="fade-up"
      >
        <div className="bg-gray-900 rounded-[3rem] p-12 md:p-20 relative overflow-hidden shadow-2xl">
          <div className="absolute top-0 right-0 w-96 h-96 bg-yellow-500 rounded-full blur-[150px] opacity-20 pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-yellow-500 rounded-full blur-[150px] opacity-20 pointer-events-none"></div>
          
          <div className="relative z-10 text-center max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-8 tracking-tight">
              Why Shop With <span className="text-yellow-400">Pak Fashion?</span>
            </h2>
            <p className="text-gray-300 leading-relaxed text-xl md:text-2xl font-medium">
              We combine affordability, quality, and sustainability. Every item in
              our store is carefully inspected and selected to ensure you get great
              style without compromising the planet. Join us in making fashion
              circular and empowering our local community.
            </p>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <div className="text-center pb-24" data-aos="zoom-in-up">
        <h2 className="text-3xl font-black text-gray-900 mb-8">Ready to step into style?</h2>
        <Link to="/contact" aria-label="Contact Us Page">
          <button className="inline-flex items-center gap-3 bg-yellow-500 hover:bg-yellow-600 text-black px-12 py-5 rounded-full text-lg font-black shadow-lg shadow-yellow-500/30 hover:shadow-xl hover:shadow-yellow-500/40 hover:-translate-y-1 transition-all duration-300">
            <FaPhoneAlt />
            Get In Touch With Us
          </button>
        </Link>
      </div>
    </div>
  );
}

export default About;
